/**
 * Primary 1-6 page content (incl. per-level primary FAQ set).
 *
 * Extracted from the former monolithic `buildLevelsContent` / adapter so the six
 * primary page chunks pull only primary content + FAQs. Shared by Primary1..6Page
 * and composed by the `buildLevelsContent` barrel.
 */
import { levelPageIds } from './pageIds';
import {
  buildPrimaryLevelContent,
  type PrimaryLevelNumber,
} from './primaryLevelContent';
import { PRIMARY_TOPICS_BY_LEVEL } from './primaryTopics';
import type { LevelPageContent } from './types';

const PRIMARY_FAQS_BY_LEVEL: Record<
  number,
  Array<{ question: string; answer: string }>
> = {
  1: [
    {
      question: 'What does Primary 1 Math cover in Singapore?',
      answer:
        'The MOE Primary 1 Mathematics syllabus covers numbers and number bonds within 10 then 20, place value within 100, addition and subtraction with renaming, simple multiplication and division stories, basic shapes (square, circle, triangle, rectangle), length in centimetres, money up to $1, reading time to the half-hour, and picture graphs. Bar models and formal multiplication tables are not yet introduced — those come in Primary 2.',
    },
    {
      question: 'Are there exams in Primary 1 Math?',
      answer:
        'Since 2019, MOE has removed all weighted exams from Primary 1 — there are no SA1 or SA2 papers, and no Weighted Assessments either. Primary 1 Math is assessed only through small non-weighted bite-sized check-ins throughout the year. The focus is on building Math routines and confidence, not on producing an exam grade.',
    },
    {
      question:
        'My child just left kindergarten — when should we start P1 Math tuition?',
      answer:
        'There is no single right time. If your child is comfortable with the first few months of Primary 1 Math at school, weekly tuition is usually not urgent. If they are struggling with number bonds or the shift from play-based learning to written work, starting in Term 1 prevents small gaps from compounding.',
    },
    {
      question: 'What are number bonds and why do they matter so much?',
      answer:
        'A number bond shows how a number can be split into parts — for example, 7 can be split into 5 + 2, 4 + 3, or 6 + 1. The Singapore Math approach uses number bonds as the foundation for addition, subtraction, and later mental arithmetic. Students who internalise them in Primary 1 develop fluent computation; students who do not tend to rely on counting fingers for years afterward.',
    },
    {
      question:
        'My child can do the maths but cannot read the word problems — should I worry?',
      answer:
        'At Primary 1, reading and Math fluency develop in parallel. It is normal for a 7-year-old to understand the underlying Math but stumble on words like "altogether", "left", or "in all". Targeted practice that pairs vocabulary with worked examples helps both skills grow at the same time.',
    },
    {
      question: 'Can families start with a trial class first?',
      answer:
        'Yes. Families can book a free trial Primary 1 Math class to see the teaching pace, the structure of the live session, and how the targeted practice between lessons works — before deciding whether to enrol for weekly classes.',
    },
  ],
  2: [
    {
      question: 'What does Primary 2 Math cover in Singapore?',
      answer:
        'The MOE Primary 2 Mathematics syllabus covers numbers up to 1,000, addition and subtraction with renaming, the multiplication tables for 2, 3, 4, 5 and 10 (and the corresponding division facts), fractions as halves and quarters, length in metres, mass in kilograms and grams, money up to $100, reading time to the minute, picture graphs with scales, and 2D / 3D shapes. Bar models begin in Primary 2 and are used to solve two-step word problems.',
    },
    {
      question: 'Are there exams in Primary 2 Math?',
      answer:
        'Since 2019, MOE has removed all weighted exams from Primary 2 — there are no SA1 or SA2 papers, and no Weighted Assessments either. Primary 2 is assessed only through small non-weighted bite-sized check-ins throughout the year. The year is treated as the time to lock in fluency rather than to produce exam grades.',
    },
    {
      question: 'When are multiplication tables introduced?',
      answer:
        'Primary 2 introduces the multiplication tables for 2, 3, 4, 5 and 10 first, with the harder 6, 7, 8, 9 tables coming in Primary 3. Primary 2 students who fall behind on the easier tables tend to struggle visibly with division and word problems for the rest of the year.',
    },
    {
      question: 'How is bar modelling taught in Primary 2?',
      answer:
        'Bar models start at Primary 2 with simple part-whole and comparison shapes. Children learn to draw a bar for every word problem, even ones that do not strictly need it, so the habit is in place when the more complex models appear in Primary 3 and beyond.',
    },
    {
      question:
        'My child knows the answer but cannot show working — what should we do?',
      answer:
        'Showing working is its own skill that schools assess separately from "getting the right answer". Lessons that explicitly model how to lay out steps, leave space for bar models, and present a final answer correctly close this gap quickly — usually within a term of consistent practice.',
    },
    {
      question: 'Should we start tuition in Primary 2 or wait until Primary 3?',
      answer:
        'Primary 2 is often the better starting point. Primary 3 introduces fractions, the harder multiplication tables, and the first formal Math papers (termly Weighted Assessments and the year-end SA2) all at once. Students who arrive at Primary 3 with multiplication and bar-model fluency already in place find the year far less stressful than students who try to catch up while learning new content.',
    },
    {
      question: 'Can families start with a trial class first?',
      answer:
        'Yes. Families can book a free trial Primary 2 Math class to see the teaching pace, the structure of the live session, and how the targeted practice between lessons works — before committing to weekly classes.',
    },
  ],
  3: [
    {
      question: 'What does Primary 3 Math cover in Singapore?',
      answer:
        'The MOE Primary 3 Mathematics syllabus covers numbers up to 10,000, addition and subtraction with renaming, the multiplication tables for 6, 7, 8 and 9, division with and without remainder, fractions as numbers (equivalent fractions, simplifying, comparing, addition and subtraction with the same denominator), perimeter, angles (acute / right / obtuse), perpendicular and parallel lines, bar graphs, length in km/m and m/cm, mass in kg/g, volume in millilitres and litres, money problems, and 24-hour time.',
    },
    {
      question: 'Why is Primary 3 considered a turning point?',
      answer:
        'Three things happen in Primary 3 that did not happen in Primary 1 or 2: fractions are introduced as proper numbers, the harder multiplication tables (6 to 9) are layered on top of earlier ones, and most schools run their first formal Math papers — termly Weighted Assessments and a year-end SA2 paper. Students who arrive with weak Primary 2 fluency feel all three at once, which is why parents often notice grades slipping in Primary 3 even when their child seemed fine before.',
    },
    {
      question: 'How important are Weighted Assessments and SA2 in Primary 3?',
      answer:
        'Primary 3 Weighted Assessments and the year-end SA2 do not affect secondary school posting (mid-year SA1 was removed for Primary 3 in 2021), but they matter as the first proper exam experience. Schools also use Primary 3 results to start identifying students for additional support or for stretch programmes. Strong Primary 3 results establish momentum and self-belief that carries into Primary 4 and 5.',
    },
    {
      question: 'My child is struggling with fractions — is that normal?',
      answer:
        'Yes — the leap from "fractions as halves and quarters" in Primary 2 to "fractions as numbers you can add, simplify, and compare" in Primary 3 is one of the biggest conceptual jumps in the entire primary syllabus. The fix is visual: bar models and number lines first, then symbols. Students who learn fractions only as rules tend to forget them; students who build a visual mental model retain them.',
    },
    {
      question: 'How does bar modelling change at Primary 3?',
      answer:
        'Primary 3 bar models start including comparison structures (one bar longer than another) and multi-step part-whole models. Students who only learned the basic "two parts make a whole" model at Primary 2 need to extend their toolkit. Lessons explicitly teach which bar shape to draw for which question type.',
    },
    {
      question: 'Should we start tuition because grades are dropping?',
      answer:
        'A drop in Primary 3 grades almost always signals one of three things: shaky multiplication tables, weak fraction understanding, or under-developed bar-model habits. A diagnostic trial class identifies which, so support targets the actual cause rather than vague "more practice".',
    },
    {
      question: 'Can families start with a trial class first?',
      answer:
        'Yes. Families can book a free trial Primary 3 Math class to see the teaching pace, the structure of the live session, and how the targeted practice between lessons works — before deciding whether to enrol for weekly classes.',
    },
  ],
  4: [
    {
      question: 'What does Primary 4 Math cover in Singapore?',
      answer:
        'The MOE Primary 4 Mathematics syllabus covers numbers up to 100,000, multiplication by 2-digit numbers, division by a 1-digit divisor, factors and multiples, mixed numbers and improper fractions, fraction addition and subtraction with related denominators, decimals to 4 decimal places (multiplying and dividing by 10 / 100 / 1,000), area and perimeter of rectangles and squares, line symmetry, line graphs, pie charts, and nets of solids. Bar models extend to representing fractional parts of a whole.',
    },
    {
      question:
        'Is Primary 4 the right time to consider tuition for PSLE preparation?',
      answer:
        'Yes — Primary 4 is the most strategic year of the lower-primary phase. Primary 5 introduces percentage, rate, and fraction division, which assume fluent fractions, decimals, and bar-model thinking. Closing gaps in Primary 4 is far cheaper than closing them in Primary 5 alongside new content. Many parents who start tuition in Primary 5 wish they had started in Primary 4.',
    },
    {
      question:
        'How does Subject-Based Banding (SBB) affect Primary 4 students?',
      answer:
        'Most Singapore primary schools use Primary 4 year-end results as the main input into the Standard or Foundation Mathematics placement for Primary 5. The conversation usually happens at the parent-teacher meeting after the SA2 results. A child whose Primary 4 Math grades sit on the boundary benefits most from a strong final term — this is often when targeted support pays the largest dividend.',
    },
    {
      question:
        'What is the difference between Foundation Math and Standard Math?',
      answer:
        'Standard Mathematics is the regular MOE syllabus that most students take through Primary 5, Primary 6, and into PSLE. Foundation Mathematics is a smaller syllabus designed for students who would otherwise struggle with Standard, allowing them to build core competencies at a more manageable pace. DeepThink classes follow the Standard Mathematics syllabus.',
    },
    {
      question: 'My child finds decimals and fractions hard — is that common?',
      answer:
        'Very common. Primary 4 is the first year decimals and fractions are taught as related ideas (½ = 0.5 = 50%) and used together in word problems. Students who learned each topic separately struggle when problems mix them. The fix is integrated practice that deliberately bridges the three forms, not more drilling on each in isolation.',
    },
    {
      question:
        'How do schools decide between Standard and Foundation Math at the end of Primary 4?',
      answer:
        'Schools weigh Primary 4 Math performance (especially SA2), classroom progress through the year, and teacher recommendation. The decision is shared with parents but the final say on Standard vs Foundation usually rests with the school. Parents can discuss the recommendation if they disagree, but the school is signalling what it believes will best support the child through PSLE.',
    },
    {
      question: 'Can families start with a trial class first?',
      answer:
        'Yes. Families can book a free trial Primary 4 Math class to see the teaching pace, the structure of the live session, and how the targeted practice between lessons works — before deciding whether to enrol for weekly classes.',
    },
  ],
  5: [
    {
      question: 'What does Primary 5 Math cover in Singapore?',
      answer:
        'The MOE Primary 5 Standard Mathematics syllabus covers whole numbers up to 10 million, multi-step decimal operations (× and ÷), fractions (multiplication, division, mixed operations), percentage (% of, % change, real-world applications), rate problems, area of triangle, volume of cuboid and liquid, angles, and the properties of triangles, parallelograms, rhombuses and trapeziums. Ratio, average and algebra all come in Primary 6, built on the Primary 5 fraction and percentage work.',
    },
    {
      question: 'Is Primary 5 really the most critical year before PSLE?',
      answer:
        'Most experienced teachers would say yes. Primary 5 introduces the topics that account for the bulk of PSLE Paper 2 long-answer marks — percentage, rate, and advanced fractions — and they are what Primary 6 ratio and algebra are built on. A student who finishes Primary 5 secure on these topics enters Primary 6 ready for PSLE preparation. A student who finishes Primary 5 shaky on them spends most of Primary 6 catching up while also learning new material.',
    },
    {
      question:
        'What is the difference between Standard and Foundation Math at Primary 5?',
      answer:
        'Standard Mathematics is the regular MOE syllabus that leads to the standard PSLE Math paper. Foundation Mathematics is a smaller syllabus for students who would otherwise struggle with Standard, with its own dedicated PSLE Foundation Math paper. The placement is normally decided at the end of Primary 4 by the school. DeepThink classes follow the Standard Mathematics syllabus.',
    },
    {
      question: 'How does percentage differ from a fraction and a decimal?',
      answer:
        'They are three ways of writing the same relationship. A fraction shows a part of one whole, a decimal writes that part in place-value form, and a percentage writes it against a base of 100 — so one-half, 0.5 and 50% are the same quantity. Many Primary 5 students are fluent in each form on its own and then stall when a single question moves between them; strong teaching makes the conversions explicit and shows which form is easiest to work in. Ratio, which compares quantities to each other rather than to one whole, arrives in Primary 6 and leans on exactly this understanding.',
    },
    {
      question:
        'My child was strong in Primary 1–4 but is struggling in Primary 5 — what is happening?',
      answer:
        'This is one of the most common patterns parents see. Primary 1–4 Math rewards procedural fluency, but Primary 5 percentage, rate, and fraction problems require multi-step reasoning that fluency alone does not solve. The fix is explicit problem-solving coaching — reading the question, sketching a bar model, planning the steps — not just more practice.',
    },
    {
      question: 'When should Primary 5 students start PSLE preparation?',
      answer:
        'Effective PSLE preparation actually begins in Primary 5, not Primary 6. Students who treat Primary 5 percentage, rate, and fractions seriously, and who build bar-model and heuristic fluency across the year, enter Primary 6 already PSLE-ready. Primary 6 then becomes a year of consolidation and timed practice rather than panicked catch-up.',
    },
    {
      question: 'Can families start with a trial class first?',
      answer:
        'Yes. Families can book a free trial Primary 5 Math class to see the teaching pace, the structure of the live session, and how the targeted practice between lessons works — before committing to weekly classes.',
    },
  ],
  6: [
    {
      question: 'What does Primary 6 Math cover in Singapore?',
      answer:
        'The MOE Primary 6 Standard Mathematics syllabus covers algebra (using letters and solving simple equations), advanced fractions, ratio with units that change before and after a transaction, percentage applied to discount / GST / interest, average, area and circumference of circle (and semicircle and quadrant), volume of cube and cuboid, angles in geometrical figures, and the multi-step word problems that combine all of the above. Each topic is taught once, then woven into PSLE-style practice for the rest of the year.',
    },
    {
      question: 'How is Primary 6 different from PSLE Math?',
      answer:
        'Primary 6 covers the syllabus topics; PSLE is the national exam at the end of Primary 6 that tests them. The overlap is large but not total — PSLE also draws on Primary 5 content and tests students with mixed-topic Paper 2 questions of a kind not always seen in school SA papers. Effective Primary 6 tuition treats syllabus completion and PSLE preparation as a single integrated programme.',
    },
    {
      question: 'When should serious PSLE preparation start in Primary 6?',
      answer:
        'From the very first lesson. PSLE-style heuristics, bar-model patterns, and timed Paper 1 / Paper 2 practice should run alongside the Primary 6 syllabus across the whole year, not be saved for the final term. Students who only start serious PSLE prep after the mid-year SA1 often run out of runway to fix the gaps that paper exposed.',
    },
    {
      question: 'What are the most heavily tested PSLE Math topics?',
      answer:
        'Fractions, ratio, percentage, and rate dominate the Paper 2 long-answer section, where the highest mark-per-question values sit. Geometry and measurement (area, perimeter, volume, angles) contribute consistently across both papers. Whole-number operations are heavily tested in Paper 1, where calculator-free fluency directly determines how many marks a student can secure under time pressure.',
    },
    {
      question:
        'My child can do textbook questions but freezes in mock exams — why?',
      answer:
        'This is almost always an exam-condition gap, not a knowledge gap. Textbook practice is usually one topic at a time, untimed, with a fresh mind. PSLE Paper 2 mixes topics, runs to the clock, and lands in the middle of an exam day. The fix is regular timed mixed-topic practice that mimics those conditions, so the student trains the actual skill the exam tests.',
    },
    {
      question: 'How does AL grading work for PSLE Math?',
      answer:
        'PSLE Math is graded on the Achievement Level scale from AL 1 to AL 8. AL 1 = 90 marks and above, AL 2 = 85–89, AL 3 = 80–84, AL 4 = 75–79, AL 5 = 65–74, AL 6 = 45–64, AL 7 = 20–44, and AL 8 = below 20. The Math AL is added to the ALs of the other PSLE subjects to produce the total PSLE Score, which is the main input into secondary school posting.',
    },
    {
      question: 'Should we focus on weak topics or do full papers?',
      answer:
        'Both, but in the right order. Until the syllabus is complete (usually around the start of Term 3), focus is on topical mastery and closing weak-topic gaps. From Term 3 onward, full timed papers should run regularly so the student trains exam stamina and pacing. Doing only one or the other tends to plateau a student short of their AL ceiling.',
    },
    {
      question: 'Can families start with a trial class first?',
      answer:
        'Yes. Families can book a free trial Primary 6 Math class to see the teaching pace, the structure of the live session, and how the targeted practice between lessons works — before committing to weekly classes.',
    },
  ],
};

export function buildPrimaryContent(
  level: string,
  brandName = 'DeepThink'
): LevelPageContent {
  const primaryLevelContent = buildPrimaryLevelContent(brandName);
  const levelNum = parseInt(level.replace('primary', '')) as PrimaryLevelNumber;
  const data = primaryLevelContent[levelNum];

  return {
    id: levelPageIds[level],
    slug: `/${levelPageIds[level]}`,
    seo: {
      title: data.seoTitle,
      description: data.seoDescription,
    },
    hero: {
      eyebrow: data.heroEyebrow,
      headline: data.heroHeadline,
      subhead: data.heroSubhead,
      ctaLabel: data.heroCtaLabel,
    },
    approach: {
      title: data.approachTitle,
      body: data.approachIntro,
      points: data.approachPoints,
      closingLine: data.approachClosingLine,
    },
    ctaSection: {
      title: 'Start with a trial class',
      body: data.ctaSectionBody,
      ctaLabel: 'Book your free trial',
    },
    challenges: {
      title: 'Common challenges at this level',
      body: data.challengesIntro,
      items: data.challenges,
    },
    ...(PRIMARY_TOPICS_BY_LEVEL[levelNum] && {
      syllabus: {
        title: 'What we cover',
        eyebrow: 'Syllabus',
        body: data.syllabusBody,
        topics: PRIMARY_TOPICS_BY_LEVEL[levelNum],
        note: data.syllabusNote,
      },
    }),
    ...(PRIMARY_FAQS_BY_LEVEL[levelNum] && {
      faqItems: PRIMARY_FAQS_BY_LEVEL[levelNum],
    }),
  };
}
